import { z } from "zod"

// Response shapes for the internal platform-admin console (`/v1/admin/*`).
// Defined here so the API route and the api-client share one contract.

export const ApiAdminUserSchema = z.object({
  userId: z.string(),
  email: z.string().nullable(),
  name: z.string().nullable(),
  imageUrl: z.string().nullable(),
  createdAt: z.string(),
  /** Active memberships. Separates one customer from a multi-client practice. */
  organizationCount: z.number(),
})
export type ApiAdminUser = z.infer<typeof ApiAdminUserSchema>

export const ApiAdminUserSearchResponseSchema = z.object({
  users: z.array(ApiAdminUserSchema),
})
export type ApiAdminUserSearchResponse = z.infer<
  typeof ApiAdminUserSearchResponseSchema
>

export const ApiAdminOrganizationSchema = z.object({
  organizationId: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  billingEmail: z.string().nullable(),
  archivedAt: z.string().nullable(),
  createdAt: z.string(),
})
export type ApiAdminOrganization = z.infer<typeof ApiAdminOrganizationSchema>

// A directory row. Everything here answers a question an operator asks before
// opening the org: what are they on, how big are they, are they still active.
// `planId` is null for an org that never subscribed, which is itself a state.
export const ApiAdminOrganizationRowSchema = ApiAdminOrganizationSchema.extend({
  planId: z.string().nullable(),
  status: z.string().nullable(),
  trialEndsAt: z.string().nullable(),
  memberCount: z.number(),
  documentCount: z.number(),
  lastDocumentAt: z.string().nullable(),
})
export type ApiAdminOrganizationRow = z.infer<
  typeof ApiAdminOrganizationRowSchema
>

export const ApiAdminOrganizationSearchResponseSchema = z.object({
  organizations: z.array(ApiAdminOrganizationRowSchema),
})
export type ApiAdminOrganizationSearchResponse = z.infer<
  typeof ApiAdminOrganizationSearchResponseSchema
>

export const ApiAdminOrganizationMemberSchema = z.object({
  userId: z.string(),
  role: z.string(),
  createdAt: z.string(),
  email: z.string().nullable(),
  name: z.string().nullable(),
})
export type ApiAdminOrganizationMember = z.infer<
  typeof ApiAdminOrganizationMemberSchema
>

// Billing state as the console shows it: the plan's own allowance, the operator
// grant on top, and what has actually been used against the combined cap.
export const ApiAdminSubscriptionSchema = z.object({
  planId: z.string(),
  billingPeriod: z.string(),
  status: z.string(),
  trialEndsAt: z.string().nullable(),
  currentPeriodStart: z.string().nullable(),
  /** When the page allowance next resets. On a yearly plan this is the next
   * monthly anniversary, not currentPeriodEnd, and it is the span pagesUsed
   * below is counted over. */
  allowanceResetsAt: z.string().nullable(),
  currentPeriodEnd: z.string().nullable(),
  cancelAt: z.string().nullable(),
  stripeCustomerId: z.string().nullable(),
  pagesUsed: z.number(),
  planPagesIncluded: z.number().nullable(),
  pagesIncluded: z.number().nullable(),
  bonusPagesGranted: z.number(),
  bonusPagesRemaining: z.number(),
})
export type ApiAdminSubscription = z.infer<typeof ApiAdminSubscriptionSchema>

export const ApiAdminWorkspaceSchema = z.object({
  workspaceId: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  archivedAt: z.string().nullable(),
  createdAt: z.string(),
  documentCount: z.number(),
})
export type ApiAdminWorkspace = z.infer<typeof ApiAdminWorkspaceSchema>

export const ApiAdminOrganizationOverviewResponseSchema = z.object({
  organization: ApiAdminOrganizationSchema,
  members: z.array(ApiAdminOrganizationMemberSchema),
  /** Null when the org never subscribed. */
  subscription: ApiAdminSubscriptionSchema.nullable(),
  workspaces: z.array(ApiAdminWorkspaceSchema),
  /** Document counts by derived state, including `deleted`. */
  documentStates: z.array(z.object({ state: z.string(), count: z.number() })),
})
export type ApiAdminOrganizationOverviewResponse = z.infer<
  typeof ApiAdminOrganizationOverviewResponseSchema
>

// One person, and every tenant they can reach. The membership carries the org's
// plan state so an operator can tell from this page alone which of a practice's
// books is the one in trouble.
export const ApiAdminUserMembershipSchema = z.object({
  organizationId: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  billingEmail: z.string().nullable(),
  archivedAt: z.string().nullable(),
  role: z.string(),
  joinedAt: z.string(),
  deactivatedAt: z.string().nullable(),
  planId: z.string().nullable(),
  status: z.string().nullable(),
  trialEndsAt: z.string().nullable(),
  documentCount: z.number(),
})
export type ApiAdminUserMembership = z.infer<
  typeof ApiAdminUserMembershipSchema
>

export const ApiAdminUserOverviewResponseSchema = z.object({
  user: ApiAdminUserSchema,
  memberships: z.array(ApiAdminUserMembershipSchema),
})
export type ApiAdminUserOverviewResponse = z.infer<
  typeof ApiAdminUserOverviewResponseSchema
>

// The audit trail. Append-only server-side; this is the read the console shows
// so an operator can see what was already done before doing it again.
export const ApiAdminAuditEventSchema = z.object({
  id: z.string().uuid(),
  actorUserId: z.string(),
  actorEmail: z.string().nullable(),
  action: z.string(),
  targetOrganizationId: z.string().uuid().nullable(),
  targetUserId: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()),
  createdAt: z.string(),
})
export type ApiAdminAuditEvent = z.infer<typeof ApiAdminAuditEventSchema>

export const ApiAdminAuditListResponseSchema = z.object({
  events: z.array(ApiAdminAuditEventSchema),
})
export type ApiAdminAuditListResponse = z.infer<
  typeof ApiAdminAuditListResponseSchema
>
export const ApiAdminDocumentSchema = z.object({
  documentId: z.string().uuid(),
  title: z.string().nullable(),
  derivedState: z.string(),
  workspaceId: z.string().uuid(),
  archivedAt: z.string().nullable(),
  createdAt: z.string(),
})
export type ApiAdminDocument = z.infer<typeof ApiAdminDocumentSchema>

export const ApiAdminDocumentListResponseSchema = z.object({
  documents: z.array(ApiAdminDocumentSchema),
})
export type ApiAdminDocumentListResponse = z.infer<
  typeof ApiAdminDocumentListResponseSchema
>

// Result of the operator "reset trial" action. Mirrors the reset subscription.
export const ApiAdminResetTrialResponseSchema = z.object({
  organizationId: z.string().uuid(),
  status: z.string(),
  planId: z.string(),
  trialEndsAt: z.string().nullable(),
  currentPeriodEnd: z.string().nullable(),
})
export type ApiAdminResetTrialResponse = z.infer<
  typeof ApiAdminResetTrialResponseSchema
>

// Operator page grant. Pages are a positive whole number; the cap is a
// guardrail against a slipped keystroke turning into a five-figure gift, not a
// policy limit — grant twice if you really mean it.
export const ApiAdminGrantPagesBodySchema = z.object({
  pages: z.number().int().positive().max(100_000),
  /** Free-text reason, kept on the audit event so a grant can be explained
   * later. Optional: the grant is recorded either way. */
  reason: z.string().trim().max(500).optional(),
})
export type ApiAdminGrantPagesBody = z.infer<
  typeof ApiAdminGrantPagesBodySchema
>

// Result of a page grant: the pool after the grant, plus the allowance it buys.
export const ApiAdminGrantPagesResponseSchema = z.object({
  organizationId: z.string().uuid(),
  /** Cumulative pages ever granted to this organization. */
  bonusPagesGranted: z.number(),
  /** Cumulative granted pages already spent. */
  bonusPagesConsumed: z.number(),
  /** Unspent grant. Carries across billing periods until used. */
  bonusPagesRemaining: z.number(),
  /** The plan's own allowance (null = unlimited). */
  planPagesIncluded: z.number().nullable(),
  /** Plan allowance plus the unspent grant (null = unlimited). */
  pagesIncluded: z.number().nullable(),
})
export type ApiAdminGrantPagesResponse = z.infer<
  typeof ApiAdminGrantPagesResponseSchema
>
// Headline platform metrics for the admin overview.
/** One reason an account wants an operator, with the accounts behind it. The
 * ids are a sample so the console can open them; `count` is the whole set. */
export const adminAttentionKeys = [
  "over_page_cap",
  "past_due",
  "quickbooks_needs_reconnect",
  "documents_failed_twice",
  "trial_ending_soon",
] as const
export type AdminAttentionKey = (typeof adminAttentionKeys)[number]

export const ApiAdminAttentionSignalSchema = z.object({
  key: z.enum(adminAttentionKeys),
  count: z.number().int().nonnegative(),
  organizationIds: z.array(z.string().uuid()).max(5),
})
export type ApiAdminAttentionSignal = z.infer<
  typeof ApiAdminAttentionSignalSchema
>

export const ApiAdminMetricsResponseSchema = z.object({
  organizations: z.number(),
  users: z.number(),
  documents: z.number(),
  subscriptionsByStatus: z.record(z.string(), z.number()),
  /** Every signal, in `adminAttentionKeys` order, even when its count is 0. */
  attention: z.array(ApiAdminAttentionSignalSchema),
})
export type ApiAdminMetricsResponse = z.infer<
  typeof ApiAdminMetricsResponseSchema
>
